// Unified data access. Pages import from here, never from supabase directly,
// so DEMO mode (mock) and live mode (Supabase + RLS) share one surface.
// Each function returns the data and throws on error.

import { IS_DEMO } from './config.js';
import { supabase } from './supabase.js';
import { demoApi } from './demo.js';

const SUMMARY_COLS =
  'id, emp_code, nickname, first_name, last_name, status, branch, position, start_date, tenure_days, active_warnings, late_count_ytd, absent_count_ytd';

function unwrap({ data, error }) {
  if (error) throw new Error(error.message);
  return data;
}

export async function listSummaries() {
  if (IS_DEMO) return demoApi.listSummaries();
  return unwrap(
    await supabase.from('v_employee_summary').select(SUMMARY_COLS).order('nickname', { nullsFirst: false })
  );
}

export async function getSummary(id) {
  if (IS_DEMO) return demoApi.getSummary(id);
  return unwrap(await supabase.from('v_employee_summary').select('*').eq('id', id).maybeSingle());
}

export async function getWarnings(employeeId) {
  if (IS_DEMO) return demoApi.getWarnings(employeeId);
  return unwrap(
    await supabase
      .from('warnings')
      .select('id, ladder_level, level_name, incident_date, expires_at, status, ack_status, incident_detail')
      .eq('employee_id', employeeId)
      .eq('status', 'active')
      .order('incident_date', { ascending: false })
  );
}

export async function getNotes(employeeId) {
  if (IS_DEMO) return demoApi.getNotes(employeeId);
  return unwrap(
    await supabase
      .from('performance_notes')
      .select('id, note_type, title, occurred_on')
      .eq('employee_id', employeeId)
      .order('occurred_on', { ascending: false })
      .limit(8)
  );
}

export async function getWarning(id) {
  if (IS_DEMO) return demoApi.getWarning(id);
  return unwrap(
    await supabase
      .from('warnings')
      .select('id, employee_id, ladder_level, level_name, incident_date, expires_at, status, ack_status, acknowledged_at, witness_name, incident_detail, issued_at')
      .eq('id', id)
      .maybeSingle()
  );
}

// Staff acknowledges their own warning — goes through the security-definer RPC
// hr.acknowledge_warning() (only flips the ack fields; cannot touch ladder/status).
export async function acknowledgeWarning(id, { refuse = false, witness = null } = {}) {
  if (IS_DEMO) return demoApi.acknowledgeWarning(id, { refuse, witness });
  return unwrap(
    await supabase.rpc('acknowledge_warning', { p_warning_id: id, p_refuse: refuse, p_witness: witness })
  );
}

export async function listPolicyClauses() {
  if (IS_DEMO) return demoApi.listPolicyClauses();
  return unwrap(
    await supabase.from('policy_clauses').select('id, clause_no, category, title').eq('active', true).order('clause_no')
  );
}

// Suggest the ladder level: highest active level for this category + 1 (cap 4).
// Human-in-the-loop — the manager confirms/overrides before issuing.
export async function suggestLadder(employeeId, category) {
  if (IS_DEMO) return demoApi.suggestLadder(employeeId, category);
  const rows = unwrap(
    await supabase
      .from('warnings')
      .select('ladder_level, policy_clauses(category)')
      .eq('employee_id', employeeId).eq('status', 'active')
  );
  const prior = (rows || []).filter((w) => w.policy_clauses?.category === category);
  const maxLvl = prior.reduce((m, w) => Math.max(m, w.ladder_level || 0), 0);
  return { level: Math.min(4, maxLvl + 1), prior_count: prior.length };
}

// Learning point — coaching record, NOT a penalty (no ladder, no clause).
export async function issueLearningPoint(payload) {
  if (IS_DEMO) return demoApi.issueLearningPoint(payload);
  return unwrap(
    await supabase.from('learning_points').insert({
      employee_id: payload.employee_id,
      topic: payload.topic,
      detail: payload.detail,
      follow_up_date: payload.follow_up_date || null,
      coached_by: payload.coached_by,
    }).select().single()
  );
}

export async function issueWarning(payload) {
  if (IS_DEMO) return demoApi.issueWarning(payload);
  return unwrap(
    await supabase.from('warnings').insert({
      employee_id: payload.employee_id,
      policy_clause_id: payload.policy_clause_id,
      ladder_level: payload.ladder_level,
      level_name: payload.level_name,
      incident_date: payload.incident_date,
      incident_detail: payload.incident_detail,
      issued_by: payload.issued_by,
    }).select().single()
  );
}

// Attendance flags (synced from HR TimeTrack). Excused days (matched to an
// approved leave) are kept but flagged so the UI can show "ไม่นับเป็นความผิด".
export async function getAttendance(employeeId) {
  if (IS_DEMO) return demoApi.getAttendance(employeeId);
  return unwrap(
    await supabase
      .from('attendance_flags')
      .select('id, work_date, flag_type, minutes, is_excused, linked_leave_id, note')
      .eq('employee_id', employeeId)
      .order('work_date', { ascending: false })
      .limit(30)
  );
}

// Monthly attendance flags (ym = "YYYY-MM"). Faults vs excused both returned;
// the page separates them.
export async function getAttendanceMonth(employeeId, ym) {
  if (IS_DEMO) return demoApi.getAttendanceMonth(employeeId, ym);
  const start = `${ym}-01`;
  const end = nextMonth(ym);
  return unwrap(
    await supabase
      .from('attendance_flags')
      .select('id, work_date, flag_type, minutes, is_excused, linked_leave_id')
      .eq('employee_id', employeeId)
      .gte('work_date', start).lt('work_date', end)
      .order('work_date', { ascending: false })
  );
}

// Approved leave overlapping the month (the สิทธิ side).
export async function getLeaveMonth(employeeId, ym) {
  if (IS_DEMO) return demoApi.getLeaveMonth(employeeId, ym);
  const start = `${ym}-01`;
  const end = nextMonth(ym);
  return unwrap(
    await supabase
      .from('leave_requests')
      .select('id, start_date, end_date, days, status, leave_types(name_th, code)')
      .eq('employee_id', employeeId)
      .eq('status', 'approved')
      .lt('start_date', end).gte('end_date', start)
      .order('start_date', { ascending: false })
  );
}

function nextMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

// Compliance reminders (คร.11, PDPA). Banner shows when one is within its window.
export async function getComplianceReminders() {
  if (IS_DEMO) return demoApi.getComplianceReminders();
  return unwrap(
    await supabase.from('compliance_reminders').select('id, type, title, due_date, status').eq('status', 'pending').order('due_date')
  );
}

// ---- Decision Support (Phase 4) ----
export async function getEmploymentInfo(employeeId) {
  if (IS_DEMO) return demoApi.getEmploymentInfo(employeeId);
  return unwrap(
    await supabase.from('employment').select('base_salary, start_date, position_id')
      .eq('employee_id', employeeId).eq('is_current', true).maybeSingle()
  );
}

export async function listRecommendations() {
  if (IS_DEMO) return demoApi.listRecommendations();
  return unwrap(
    await supabase.from('recommendations')
      .select('id, employee_id, type, basis, suggested_value, manager_decision, employees(nickname, first_name)')
      .order('created_at', { ascending: false })
  );
}

// Human decides. Writes decided_by + audit (real mode).
export async function decideRecommendation(id, decision, deciderId) {
  if (IS_DEMO) return demoApi.decideRecommendation(id, decision);
  return unwrap(
    await supabase.from('recommendations')
      .update({ manager_decision: decision, decided_by: deciderId, decided_at: new Date().toISOString() })
      .eq('id', id).select().single()
  );
}

// ---- Evaluations (Phase 3) ----
export async function getCurrentCycle() {
  if (IS_DEMO) return demoApi.getCurrentCycle();
  return unwrap(
    await supabase.from('evaluation_cycles').select('id, name, period_start, period_end, status, announced_at')
      .eq('status', 'open').order('period_start', { ascending: false }).limit(1).maybeSingle()
  );
}

export async function getKpis(position, positionId) {
  if (IS_DEMO) return demoApi.getKpis(position);
  // KPI ของตำแหน่งนี้ + KPI กลาง (position_id null)
  return unwrap(
    await supabase.from('kpi_definitions').select('id, name, weight')
      .or(`position_id.eq.${positionId},position_id.is.null`).eq('active', true)
  );
}

export async function listEvaluations(cycleId) {
  if (IS_DEMO) return demoApi.listEvaluations();
  return unwrap(
    await supabase.from('evaluations')
      .select('employee_id, status, overall_score, employees(nickname, first_name)')
      .eq('cycle_id', cycleId)
  );
}

export async function getEvaluation(employeeId, cycleId) {
  if (IS_DEMO) return demoApi.getEvaluation(employeeId);
  return unwrap(
    await supabase.from('evaluations')
      .select('id, employee_id, overall_score, strengths, improvements, kpi_summary, status, acknowledged_at')
      .eq('employee_id', employeeId).eq('cycle_id', cycleId).maybeSingle()
  );
}

export async function saveEvaluation(payload) {
  if (IS_DEMO) return demoApi.saveEvaluation(payload);
  return unwrap(
    await supabase.from('evaluations').upsert({
      cycle_id: payload.cycle_id, employee_id: payload.employee_id, evaluator_id: payload.evaluator_id,
      overall_score: payload.overall_score, strengths: payload.strengths, improvements: payload.improvements,
      kpi_summary: payload.scores, status: payload.status,
      submitted_at: payload.status === 'submitted' ? new Date().toISOString() : null,
    }, { onConflict: 'cycle_id,employee_id' }).select().single()
  );
}

export async function acknowledgeEvaluation(employeeId, cycleId) {
  if (IS_DEMO) return demoApi.acknowledgeEvaluation(employeeId);
  return unwrap(
    await supabase.from('evaluations')
      .update({ status: 'acknowledged', acknowledged_at: new Date().toISOString() })
      .eq('employee_id', employeeId).eq('cycle_id', cycleId).select().single()
  );
}

// ---- Leave (Phase 1) ----
export async function listLeaveTypes() {
  if (IS_DEMO) return demoApi.listLeaveTypes();
  return unwrap(await supabase.from('leave_types').select('id, code, name_th, requires_medical_cert, notes').eq('active', true));
}

export async function getLeaveBalances(employeeId) {
  if (IS_DEMO) return demoApi.getLeaveBalances(employeeId);
  const year = Number(new Date().toISOString().slice(0, 4));
  const rows = unwrap(
    await supabase.from('leave_balances')
      .select('entitled_days, used_days, leave_types(code, name_th)')
      .eq('employee_id', employeeId).eq('year', year)
  );
  return (rows || []).map((r) => ({
    code: r.leave_types?.code, name_th: r.leave_types?.name_th,
    entitled: r.entitled_days, used: r.used_days, remaining: r.entitled_days - r.used_days,
  }));
}

export async function getMyLeaves(employeeId) {
  if (IS_DEMO) return demoApi.getMyLeaves(employeeId);
  return unwrap(
    await supabase.from('leave_requests')
      .select('id, start_date, end_date, days, status, reason, leave_types(name_th, code)')
      .eq('employee_id', employeeId).order('start_date', { ascending: false })
  );
}

export async function listPendingLeaves() {
  if (IS_DEMO) return demoApi.listPendingLeaves();
  return unwrap(
    await supabase.from('leave_requests')
      .select('id, employee_id, start_date, end_date, days, reason, medical_cert_url, leave_types(name_th, code, requires_medical_cert), employees(nickname, first_name)')
      .eq('status', 'pending').order('start_date')
  );
}

export async function requestLeave(payload) {
  if (IS_DEMO) return demoApi.requestLeave(payload);
  return unwrap(
    await supabase.from('leave_requests').insert({
      employee_id: payload.employee_id, leave_type_id: payload.leave_type_id,
      start_date: payload.start_date, end_date: payload.end_date, days: payload.days,
      reason: payload.reason,
    }).select().single()
  );
}

// Approve/reject. Human decides; writes decided_by + audit. (Real: balance
// recompute belongs in an RPC/trigger — see hr_schema.sql Phase 1 notes.)
export async function decideLeave(id, approve, deciderId) {
  if (IS_DEMO) return demoApi.decideLeave(id, approve);
  return unwrap(
    await supabase.from('leave_requests')
      .update({ status: approve ? 'approved' : 'rejected', decided_by: deciderId, decided_at: new Date().toISOString() })
      .eq('id', id).select().single()
  );
}

export async function getCommendations(employeeId) {
  if (IS_DEMO) return demoApi.getCommendations(employeeId);
  return unwrap(
    await supabase
      .from('commendations')
      .select('id, category, title, detail, reward_points, occurred_on')
      .eq('employee_id', employeeId)
      .order('occurred_on', { ascending: false })
  );
}

// Issue a commendation / record good work (+ optional reward points).
// Human-in-the-loop: a manager submits this; nothing is automatic.
export async function issueCommendation(payload) {
  if (IS_DEMO) return demoApi.issueCommendation(payload);
  return unwrap(
    await supabase
      .from('commendations')
      .insert({
        employee_id: payload.employee_id,
        category: payload.category,
        title: payload.title,
        detail: payload.detail,
        reward_points: payload.reward_points,
        occurred_on: payload.occurred_on,
        issued_by: payload.issued_by,
      })
      .select()
      .single()
  );
}
